import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";

import { CodeBlock, OutputBlock } from "../../CodeOutputBlocks";

const Delete_Rows_CS = ({
  subtopicId,
  goalName,
  courseName,
  subtopic,
}) => {
  const { markSubtopicComplete, loadProgressSummary, completedContent } =
    useAuth();

  const [isSubtopicCompleted, setIsSubtopicCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if subtopic is already completed
  useEffect(() => {
    if (completedContent.includes(subtopicId)) {
      setIsSubtopicCompleted(true);
    }
  }, [completedContent, subtopicId]);

  const handleContinue = async () => {
    if (isLoading || isSubtopicCompleted) return;

    try {
      setIsLoading(true);
      const result = await markSubtopicComplete(
        subtopicId,
        goalName,
        courseName
      );

      if (result.success) {
        await loadProgressSummary();
        setIsSubtopicCompleted(true);
        console.log("✅ Cheat sheet marked as completed");
      } else {
        console.error(
          "❌ Failed to mark cheat sheet complete:",
          result.message
        );
        alert("Failed to mark as complete. Please try again.");
      }
    } catch (error) {
      console.error("❌ Failed to mark cheat sheet complete:", error);
      alert("Failed to mark as complete. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="intro-container">
      <h1>Delete Rows | Cheat Sheet</h1>

      {/* 1. Database Overview */}
      <section>
        <h2>Database</h2>
        <p>
          The given database consists of tables that store the information of{" "}
          <b>student</b> and <b>player</b>.
        </p>

        <h3>Tables</h3>

        <p>
          <b>student</b> table
        </p>
        <table
        className="custom-table"
        >
          <thead>
            <tr>
              <th>name</th>
              <th>percentage</th>
              <th>scholarship_amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Ramesh</td>
              <td>82</td>
              <td>10000</td>
            </tr>
            <tr>
              <td>Mahesh</td>
              <td>85</td>
              <td>11000</td>
            </tr>
            <tr>
              <td>...</td>
              <td>...</td>
              <td>...</td>
            </tr>
          </tbody>
        </table>
        <p>
          The <code>student</code> table stores the data of the students,
          including <b>name</b>, <b>percentage</b>, and{" "}
          <b>scholarship_amount</b>.
        </p>

        <p>
          <b>player</b> table
        </p>
        <table
      className="custom-table"
        >
          <thead>
            <tr>
              <th>name</th>
              <th>age</th>
              <th>score</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Suresh</td>
              <td>21</td>
              <td>70</td>
            </tr>
            <tr>
              <td>Venkat</td>
              <td>21</td>
              <td>43</td>
            </tr>
            <tr>
              <td>...</td>
              <td>...</td>
              <td>...</td>
            </tr>
          </tbody>
        </table>
        <p>
          The <code>player</code> table stores the data of players, including{" "}
          <b>name</b>, <b>age</b>, and <b>score</b>.
        </p>
      </section>

      {/* 2. Delete Rows */}
      <section>
        <h2>Delete Rows</h2>
        <p>
          The <code>DELETE</code> clause is used to delete existing records from
          a table.
        </p>
      </section>

      {/* 3. Delete All Rows */}
      <section>
        <h3>Delete All Rows</h3>
        <h3>Syntax</h3>
        <CodeBlock language="sql" code={`DELETE FROM table_name;`} />

        <h3>Example</h3>
        <p>
          Delete all the rows from the <code>player</code> table.
        </p>

        <CodeBlock language="sql" code={`DELETE FROM player;`} />
      </section>

      {/* 4. Delete Specific Rows */}
      <section>
        <h3>Delete Specific Rows</h3>
        <h3>Syntax</h3>
        <CodeBlock
          language="sql"
          code={`DELETE FROM table_name
WHERE column1 = value1;`}
        />

        <h3>Example</h3>
        <p>
          Delete <b>"Shyam"</b> from the <code>player</code> table.
        </p>

        <div className="Note-container">
          <div className="icon-note">
            <h6>
              <i class="bi bi-journal-text"></i>Note
            </h6>
          </div>
          <p>In this table, we can uniquely identify a player by name.</p>
        </div>
        <CodeBlock
          language="sql"
          code={`DELETE FROM player
WHERE name = "Shyam";`}
        />

        <p>
          Check if the rows are deleted as per your queries in the{" "}
          <code>player</code> table.
        </p>
      </section>

      <div className="Warning-container">
        <div>
          <h5>
            <i class="bi bi-exclamation-triangle"></i>Warning
          </h5>
        </div>
        <p>
          We can not retrieve the data once we delete the data from the table.
        </p>
      </div>

      {/* 6. Drop Table */}
      <section>
        <h2>Drop Table</h2>
        <p>
          The <b>DROP</b> clause is used to delete a table from the database.
        </p>

        <h3>Syntax</h3>
        <CodeBlock language="sql" code={`DROP TABLE table_name;`} />

        <h3>Example</h3>
        <p>
          Delete <code>player</code> table from the database.
        </p>

        <CodeBlock language="sql" code={`DROP TABLE player;`} />

        <p>
          After you run the above query, you will not see the{" "}
          <code>player</code> table in the database.
        </p>
      </section>

      {/* 7. DELETE VS DROP */}
      <section>
        <h2>DELETE VS DROP</h2>
        <p>
          <code> DELETE</code> clause deletes <b>specific rows</b> from a table
          based on a condition (using WHERE clause).
        </p>
        <p>
          <code>DROP</code> clause deletes the entire <b>table</b> from the
          database.
        </p>
        <img
          src="/assets/img/delete_vs_drop_img.png"
          alt="DOM Tree"
          style={{ width: "70%", height: "300px" }}
        />
      </section>

      {/* Continue Button */}
     <div className="view-continue">
        <button
          className={`btn-continue ${isSubtopicCompleted ? "completed" : ""}`}
          onClick={handleContinue}
          disabled={isSubtopicCompleted || isLoading}
        >
          {isLoading
            ? "Marking..."
            : isSubtopicCompleted
            ? "✓ Completed"
            : "Continue"}
        </button>
      </div>
    </div>
  );
};

export default Delete_Rows_CS;
